import { renderDateCell } from '@/lib/listCells';
import type { CrudConfig } from '@startupquickstart/frontend';

/** Sentinel stock value that means "no cap". */
const UNLIMITED_STOCK = 0;

/**
 * Renders a collectible's default stock right-aligned, surfacing the
 * `0 = unlimited` sentinel as a word rather than a bare "0".
 *
 * @param value - The configured default stock.
 * @returns A right-aligned stock cell node.
 */
function renderStockCell(value: number | null | undefined): React.ReactNode {
  const display = value === UNLIMITED_STOCK ? 'Unlimited' : (value?.toLocaleString() ?? '—');
  return <span className="font-mono text-sm tabular-nums">{display}</span>;
}

export interface Collectible {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  iconUrl?: string | null;
  imageUrl?: string | null;
  defaultStock: number;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  } | null;
}

export const collectibleCrudConfig: CrudConfig<Collectible> = {
  entityName: 'Collectible',
  entityNamePlural: 'Collectibles',

  endpoints: {
    list: '/collectibles',
    single: (id: string) => `/collectibles/${id}`,
    create: '/collectibles',
    update: (id: string) => `/collectibles/${id}`,
    delete: (id: string) => `/collectibles/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Lobster',
      validation: { min: 1, max: 200 },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Flavor text for this collectible',
    },
    {
      key: 'iconUrl',
      label: 'Icon URL',
      type: 'text',
      required: false,
      placeholder: 'Small icon (badge size)',
    },
    {
      key: 'imageUrl',
      label: 'Image URL',
      type: 'text',
      required: false,
      placeholder: 'Hero image (modal / detail)',
    },
    {
      key: 'defaultStock',
      label: 'Default Stock',
      type: 'number',
      required: false,
      defaultValue: UNLIMITED_STOCK,
      helpText: '0 means unlimited; positive numbers are display-only in v1',
      align: 'right',
      format: renderStockCell,
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
  ],

  listView: {
    fields: ['name', 'defaultStock', 'updatedAt'],
    // Phones keep the two leading columns; the rest is desktop
    // detail (hideOnMobile) so the table never scrolls sideways.
    columns: [
      { field: 'name' },
      { field: 'defaultStock' },
      { field: 'updatedAt', hideOnMobile: true },
    ],
    searchableFields: ['name', 'description'],
    sortableFields: ['name', 'createdAt', 'updatedAt'],
    defaultSort: { field: 'updatedAt', direction: 'desc' },
    itemsPerPage: 10,
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Collectible Details',
        description: 'Basic information',
        fields: ['name', 'description', 'defaultStock'],
        defaultExpanded: true,
      },
      {
        title: 'Media',
        description: 'Icon and image',
        fields: ['iconUrl', 'imageUrl'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Collectible Details',
        description: 'Basic information about the collectible',
        fields: ['name', 'description', 'defaultStock'],
        columns: 1,
      },
      {
        title: 'Media',
        description: 'Visual assets',
        fields: ['iconUrl', 'imageUrl'],
        columns: 2,
      },
    ],
    validation: {
      name: { required: true, min: 1, max: 200 },
    },
  },
};
